// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/eventCover.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// shared/eventCover.js
//
// Resolves an event's cover media from the row.
//
// ── Why this exists ────────────────────────────────────────────────────────
//
// The cover is spread across NINE columns in three families, each with a CDN
// url, a direct (origin) url, and a storage path:
//
//   cover_image_{cdn_url,direct_url,path}   still image
//   gif_cover_{cdn_url,direct_url,path}     animated GIF
//   static_gif_{cdn_url,direct_url,path}    first frame of that GIF, used as a
//                                           poster and as the Android still
//   cover_image_type                        'image' | 'gif'
//
// Every screen picking through those by hand is how you get covers that render
// on one screen and not another. Worse, it is easy to invent a plausible-but-
// wrong name: an earlier version of the new event/home components read
// `cover_image_url`, `cover_static_url` and `cover_blurhash`, none of which are
// real columns, so those covers silently rendered nothing.
//
// Pure, no imports, shared with the website.

/** Column families, in preference order within each. */
const STILL = ['cover_image_cdn_url', 'cover_image_direct_url'];
const GIF = ['gif_cover_cdn_url', 'gif_cover_direct_url'];
const GIF_POSTER = ['static_gif_cdn_url', 'static_gif_direct_url'];

/**
 * The stored BlurHash, when there is one.
 *
 * Null for every cover uploaded before the cover_blurhash column existed, and
 * for any upload where generation failed — callers fall back to a neutral
 * placeholder, so this is always optional.
 */
export function coverBlurhash(event) {
  const v = event?.cover_blurhash;
  return typeof v === 'string' && v.trim() ? v : null;
}

/** Internal alias, kept so the existing call sites below read unchanged. */
const blurhash = coverBlurhash;

function firstUrl(event, keys) {
  for (const k of keys) {
    const v = event?.[k];
    if (typeof v === 'string' && v.trim()) return v;
  }
  return null;
}

/**
 * @typedef {{
 *   uri: string | null,       // what to display (may be animated)
 *   posterUri: string | null, // a still to paint underneath, if there is one
 *   isAnimated: boolean,
 *   placeholder: string | null,
 * }} EventCoverSource
 */

/**
 * @param {object} event
 * @returns {EventCoverSource}
 */
export function resolveEventCover(event) {
  const empty = { uri: null, posterUri: null, isAnimated: false, placeholder: null };
  if (!event) return empty;

  const still = firstUrl(event, STILL);
  const gif = firstUrl(event, GIF);
  const gifPoster = firstUrl(event, GIF_POSTER);

  // Trust the explicit type when it says gif AND a gif url is actually present;
  // a stale type with no asset must not blank the cover.
  const wantsGif = event.cover_image_type === 'gif' || (!still && !!gif);

  if (wantsGif && gif) {
    return {
      uri: gif,
      // Prefer the extracted first frame; fall back to the still if the GIF
      // predates poster extraction.
      posterUri: gifPoster ?? still ?? null,
      isAnimated: true,
      placeholder: blurhash(event),
    };
  }

  const uri = still ?? gifPoster ?? gif ?? null;
  return {
    uri,
    posterUri: uri,
    isAnimated: false,
    placeholder: blurhash(event),
  };
}

/** The lightest still available — for list rows, where GIFs must not autoplay. */
export function resolveEventThumbnail(event) {
  if (!event) return null;
  return firstUrl(event, STILL) ?? firstUrl(event, GIF_POSTER) ?? firstUrl(event, GIF);
}

/**
 * Columns a cover needs. Exported so list queries can select exactly these
 * instead of `*`, and so adding a column family means touching one place.
 */
export const COVER_COLUMNS = [
  ...STILL, ...GIF, ...GIF_POSTER, 'cover_image_type', 'cover_blurhash',
];
